import { useRef, useState, useEffect } from "react";
import { Link } from "react-router";
import { useAuth } from "../context/AuthContext";
import { createPortal } from "react-dom";
import type { Theme } from "@/config/themes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faGear,
  faWindowRestore,
  faLock,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import WorkerAvatar from "./worker/WorkerAvatar";
import { ChangePasswordDialog } from "@/components/auth/ChangePasswordDialog";
import { UserAccountMenuIdentity } from "@/components/auth/UserAccountMenuIdentity";
import { MfaEnrollDialog } from "@/components/auth/MfaEnrollDialog";

// Define Theme interface (could be imported from a shared types file)
// interface Theme {
//   name: string;
//   colors: string[];
// }

// Define props interface
interface SettingsPopoverProps {
  isCollapsed: boolean;
  availableThemes?: Theme[];
  currentTheme?: Theme;
  onSelectTheme?: (theme: Theme) => void;
  closeIcon?: React.ReactNode;
  companyId?: string;
  onToggleLayout?: () => void;
}

export function SettingsPopover({
  isCollapsed,
  availableThemes,
  currentTheme,
  onSelectTheme,
  closeIcon,
  companyId,
  onToggleLayout,
}: SettingsPopoverProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isMfaEnrollOpen, setIsMfaEnrollOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [menuHeight, setMenuHeight] = useState(180); // Initial estimate

  const handleLogout = () => {
    // Redirect to the /logout route which handles complete logout flow
    // including server-side session cleanup, localStorage/sessionStorage clearing,
    // and shows a nice logout UI with loading/success/error states
    window.location.href = "/logout";
  };

  // Calculate position when menu opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 224; // w-56 = 14rem = 224px
      const margin = 8;

      // Position to the right of the button
      const left = buttonRect.right + margin;

      // Initial position - will be refined once we have the actual menu height
      const initialTop = buttonRect.bottom - menuHeight - 4; // Small adjustment for perfect alignment

      // Ensure the menu doesn't go off the screen
      const adjustedTop = Math.max(margin, initialTop);
      const adjustedLeft = Math.min(
        left,
        window.innerWidth - menuWidth - margin
      );

      setPosition({
        top: adjustedTop,
        left: adjustedLeft,
      });
    }
  }, [isOpen, menuHeight, isCollapsed]);

  // Update position once we know the actual menu height
  useEffect(() => {
    if (isOpen && menuRef.current && buttonRef.current) {
      const actualMenuHeight = menuRef.current.getBoundingClientRect().height;
      if (actualMenuHeight !== menuHeight) {
        setMenuHeight(actualMenuHeight);
      }
    }
  }, [isOpen, menuHeight]);

  // Close the menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef, buttonRef]);

  return (
    <div className="relative w-full h-10 flex items-center">
      <button
        ref={buttonRef}
        onClick={() => {
          setIsOpen(!isOpen);
          // Reset menu height when toggling to ensure proper calculation
          if (!isOpen) {
            setMenuHeight(180); // Reset to initial estimate
          }
        }}
        className={`p-1 ${
          currentTheme?.baseTextColor === "light"
            ? "text-white hover:text-white/80"
            : "text-white hover:text-white/80"
        } flex items-center gap-x-2 rounded-lg cursor-pointer w-full`}
        title="Settings"
      >
        <WorkerAvatar
          key={user?.id ?? "anonymous"}
          seed={user?.fullname || user?.email || "User"}
          size={28}
          image={user?.image}
          user={true}
        />
        {!isCollapsed && (
          <div
            className={`relative text-sm text-left transition-opacity duration-400 flex-grow font-medium inline-block  whitespace-nowrap ${
              isCollapsed ? "opacity-0" : "opacity-100"
            }`}
          >
            {user?.fullname || user?.name || user?.email}
            <div className="text-xs font-mono mt-0.5 text-gray-500 text-left dark:text-gray-400">
              {user?.company_id
                ?.split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")}
            </div>
          </div>
        )}
        {!isCollapsed && (
          <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
        )}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: `${position.top}px`,
              left: `${position.left}px`,
              zIndex: 9999,
            }}
            className="w-64 rounded-lg bg-white shadow-xl focus:outline-none dark:bg-gray-800 dark:ring-white/10"
          >
            <div className="p-2 ">
              {user ? <UserAccountMenuIdentity user={user} className="mb-1 rounded-md" /> : null}
              <div className="py-1">
                <Link
                  to="/settings"
                  onClick={() => setIsOpen(false)}
                  className="cursor-pointer w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Settings
                  <div className="w-5 flex items-center justify-center">
                    <FontAwesomeIcon icon={faGear} />
                  </div>
                </Link>
              </div>
              {onToggleLayout && (
                <div className="py-1">
                  <button
                    onClick={() => {
                      onToggleLayout();
                      setIsOpen(false);
                    }}
                    className="cursor-pointer w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Switch to aniOS
                    <div className="w-5 flex items-center justify-center">
                      <FontAwesomeIcon icon={faWindowRestore} />
                    </div>
                  </button>
                </div>
              )}
              {user?.sso_enabled !== true && (
                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsChangePasswordOpen(true);
                      setIsOpen(false);
                    }}
                    className="cursor-pointer w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md dark:text-gray-300 dark:hover:bg-gray-700"
                    data-testid="change-password-menu-item"
                  >
                    Change password
                    <div className="w-5 flex items-center justify-center">
                      <FontAwesomeIcon icon={faLock} />
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setIsMfaEnrollOpen(true);
                      setIsOpen(false);
                    }}
                    className="cursor-pointer w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md dark:text-gray-300 dark:hover:bg-gray-700"
                    data-testid="mfa-enroll-menu-item"
                  >
                    Authenticator app
                    <div className="w-5 flex items-center justify-center">
                      <FontAwesomeIcon icon={faLock} />
                    </div>
                  </button>
                </div>
              )}
              <div className="py-1">
                <button
                  onClick={handleLogout}
                  className="cursor-pointer w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Logout
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {isChangePasswordOpen && (
        <ChangePasswordDialog
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
        />
      )}
      {isMfaEnrollOpen && (
        <MfaEnrollDialog
          isOpen={isMfaEnrollOpen}
          onClose={() => setIsMfaEnrollOpen(false)}
        />
      )}
    </div>
  );
}
